// Enumeration label lookups shared by the benefit application detail page, the household
// and income editors, and the guided create-benefit flow. The numbers are the legacy bytes
// the API reads and writes; 255 always means "not answered".

export const proofingStatusLabels: Record<number, string> = {
  0: "Draft", 5: "Certified", 6: "In Review", 10: "Complete", 20: "Proofed", 30: "Certified",
  100: "Inactive", 110: "Pre-Season No Start", 120: "Denied", 200: "Submitted", 255: "Draft",
}

export const grantStatusLabels: Record<number, string> = {
  0: "New", 1: "Created", 10: "Received", 20: "Incomplete", 30: "Certified",
  31: "Under Review", 40: "Additional Info Required", 50: "Pending Verification",
  60: "Verified", 70: "Approved", 80: "Denied (Before Finalization)",
  81: "Denied (After Finalization)", 90: "On Hold", 100: "Awaiting Applicant Response",
}

export const benefitAppTypeLabels: Record<number, string> = {
  0: "PSE", 1: "LIHEAP", 2: "Lakehaven", 3: "SHEAP", 99: "Unknown", 100: "Combined",
}

export const housingStatusLabels: Record<number, string> = {
  0: "Own", 1: "Rent", 2: "Subsidized", 3: "Room/Board", 4: "Temp Housing", 255: "Unknown",
}

export const housingTypeLabels: Record<number, string> = {
  0: "1-3 Family", 1: "4+ Family", 2: "Hi-Rise", 3: "Mobile", 4: "RV", 5: "RV w/Propane", 255: "Unknown",
}

export const genderLabels: Record<number, string> = {
  0: "Female", 1: "Male", 2: "Trans-Male", 3: "Trans-Female", 4: "Gender Non-Conforming",
}

export const ethnicityLabels: Record<number, string> = {
  0: "African American", 1: "Alaskan Native", 2: "Cambodian", 3: "East European",
  4: "Filipino", 5: "Hawaiian/Native American", 6: "Japanese", 7: "Korean",
  8: "Middle Eastern", 9: "Native American", 10: "Samoan", 11: "Somalian",
  12: "Vietnamese", 13: "White", 14: "Multi-Ethnic", 15: "Other", 16: "Sudanese",
  17: "Lao/Other SE Asia", 18: "Pacific Islander", 19: "Ethiopian/Eritrean",
  20: "China/Other E Asia", 21: "India/Peninsula", 22: "Liberia/Other Black Africa",
  23: "Hispanic Multi-Racial", 24: "Hispanic Asian", 25: "Hispanic Black",
  26: "Hispanic Pacific Islander", 27: "Hispanic White", 28: "Caribbean Non-Hispanic",
}

export const relationshipLabels: Record<number, string> = {
  0: "Self", 2: "Partner", 4: "Niece/Nephew", 6: "Sibling", 7: "Other Relative",
  8: "Other Non-Relative", 11: "Husband", 12: "Wife", 31: "Parent", 32: "Child",
  51: "Grandparent", 52: "Grandchild",
}

export const incomeSourceLabels: Record<number, string> = {
  1: "Exclusion", 2: "Employment", 3: "General Assistance", 4: "Pension",
  5: "Social Security", 6: "SSI", 7: "TANF", 8: "Unemployment", 9: "Other",
  10: "Veterans Assistance", 11: "Military", 12: "Child Support", 13: "Self-Employment",
  14: "SSDI", 15: "VA Svc", 16: "VA Non-svc", 17: "Private Disability",
  18: "Workers Comp", 19: "Alimony",
}

export const educationLabels: Record<number, string> = {
  0: "Kindergarten", 1: "1st Grade", 2: "2nd Grade", 3: "3rd Grade",
  4: "4th Grade", 5: "5th Grade", 6: "6th Grade", 7: "7th Grade",
  8: "8th Grade", 9: "9th Grade", 10: "10th Grade", 11: "11th Grade",
  12: "12th Grade", 13: "GED", 14: "HS Diploma", 15: "Some College",
  16: "Associate's", 17: "Bachelor's", 18: "No Schooling",
  19: "Tech Certificate", 20: "Graduate/Post-Secondary",
}

export const outcomeLabels: Record<number, string> = {
  0: "Off", 1: "Crisis", 2: "Past Due", 3: "Current", 4: "Credit",
}

export const workStatusLabels: Record<number, string> = {
  0: "Full-Time (30+ hrs)", 1: "Part-Time (<30 hrs)", 2: "Seeking Work",
  3: "Not Seeking Work", 4: "Seeking Work (Short-Term)", 5: "Seeking Work (Long-Term)",
  255: "Not Specified",
}

export const medicalInsuranceLabels: Record<number, string> = {
  0: "DSHS", 1: "Medicare", 2: "WA State Health", 3: "Employer Plan",
  4: "Direct Purchase", 5: "No Insurance", 6: "Military Health", 255: "Not Specified",
}

// Mirrors BenefitDtoExtensions.GetFamilyTypeLabel server-side.
// Stored values match Cms.Core/FamilyComposition.cs, not the visual ordering of options.
export const familyTypeLabels: Record<number, string> = {
  0: "Single", 1: "Single Parent — Female", 2: "Single Parent — Male",
  3: "Two Parent Household", 4: "Two Adults No Children", 5: "Other No Children",
  6: "Other with Children", 7: "Multigeneration", 8: "Non-related Adults with Children",
}

export const incorporationLabels: Record<number, string> = {
  0: "Unincorporated", 1: "Incorporated", 255: "Unknown",
}

// Mirrors ApplicationStateExtensions.GetDisplayName. Distinct from proofingStatusLabels,
// which spells the same bytes the way the legacy detail header always has.
export const applicationStateLabels: Record<number, string> = {
  0: "Draft", 5: "Certified (Legacy)", 6: "In Review", 10: "Completed", 20: "Proofed",
  30: "Certified", 100: "Inactive", 110: "Pre-Season", 120: "Denied", 200: "Submitted",
  255: "Draft",
}

export const serviceCategoryLabels: Record<number, string> = {
  0: "None", 100: "Heating", 200: "Cooling", 300: "Utility", 400: "Maintenance", 500: "Housing Expense",
}

export const heatingEnergyLabels: Record<number, string> = {
  10: "Electricity", 11: "Gas", 12: "Propane", 13: "Oil", 14: "Wood", 15: "Coal",
}

export function formatServiceLabel(svc: { Id: number; CategoryId: number } | null | undefined): string {
  if (!svc) return "—"
  const cat = serviceCategoryLabels[svc.CategoryId] ?? `Category ${svc.CategoryId}`
  if (svc.CategoryId === 100) {
    const energy = heatingEnergyLabels[svc.Id] ?? `#${svc.Id}`
    return `${cat} — ${energy}`
  }
  return `${cat} (#${svc.Id})`
}

export const twoYearCertStateLabels: Record<number, string> = {
  0: "Not Answered", 1: "Opted In", 2: "Opted Out",
}

export const pseApplicationTypeLabels: Record<number, string> = {
  0: "Regular", 1: "SDH with Demographics", 2: "SDH no Demographics",
  3: "Verified Qualified", 4: "Verified Ineligible",
}

export const pseAppearanceOnBillLabels: Record<number, string> = {
  0: "Primary", 1: "Co-customer", 2: "Not Listed",
}

export const pseEnergyTypeLabels: Record<number, string> = {
  0: "All Electric", 1: "Gas and Electric", 2: "Gas Only", 3: "Electric Base",
}

export const billingTypeLabels: Record<number, string> = {
  1: "Annual", 6: "Semi-Monthly", 12: "Monthly",
}

export const eligibilityLabels: Record<number, string> = {
  1: "Eligible", 2: "Not Eligible",
}

export const programTypeLabels: Record<number, string> = {
  0: "PSE", 1: "LIHEAP", 2: "Other", 3: "Lakehaven", 4: "Appointment", 196: "SHEAP",
}

// BenefitProgram IDs (used in BenefitGrant.BenefitProgramId)
export const benefitProgramLabels: Record<number, string> = {
  0: "PSE Help", 1: "LIHEAP", 2: "Lakehaven", 3: "SHEAP",
  4: "LIHEAP Contingency", 5: "Arrears", 6: "Rent Assistance",
  7: "Water Assistance", 8: "Sewer Assistance", 9: "Cooling Assistance",
  10: "Water/Sewer Assistance", 11: "LIHEAP Supplemental",
}

// BenefitType labels (used on BenefitEntity.BenefitType — year-specific)
export function getProgramLabel(t: number): string {
  if (programTypeLabels[t]) return programTypeLabels[t]
  if (t >= 5 && t <= 48) return t % 2 === 1 ? "PSE" : "LIHEAP"
  return `Program ${t}`
}

export function getBenefitProgramLabel(id: number): string {
  return benefitProgramLabels[id] ?? `Program ${id}`
}

/**
 * Turns a label lookup into `<Select>` options. The legacy 255 sentinel always leads the
 * list and always reads as "not answered", whatever the lookup happens to call it, because
 * that is what it means everywhere the API uses it.
 */
export function selectOptions(
  labels: Record<number, string>,
  notAnsweredLabel = "Not answered",
): { value: string; label: string }[] {
  const options = Object.entries(labels)
    .filter(([value]) => value !== "255")
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => Number(a.value) - Number(b.value))
  return [{ value: "255", label: notAnsweredLabel }, ...options]
}
